import { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RECOVERY_CODES_STRINGS as S } from '@/lib/constants/two-factor-strings';
import { downloadBlob } from '@/lib/utils/download.utils';

export interface RecoveryCodesListProps {
  codes: readonly string[];
}

/**
 * One-time 2FA recovery codes with copy and download (MYN-241). Shared by the
 * post-setup screen and the account page's regenerate dialog. The codes exist
 * only in memory here: they are shown once and never cached or stored by the
 * app; the download is a local file built in the browser.
 */
export function RecoveryCodesList({ codes }: RecoveryCodesListProps) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const text = codes.join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // Clipboard can be blocked (permissions, insecure context). The codes
      // are still on screen and downloadable, so say so rather than fail silently.
      setCopyFailed(true);
    }
  };

  const handleDownload = () => {
    downloadBlob(new Blob([`${text}\n`], { type: 'text/plain' }), S.DOWNLOAD_FILENAME);
  };

  return (
    <div className="flex flex-col gap-4">
      <ul
        aria-label={S.LIST_LABEL}
        className="rounded-card border-navy/10 bg-page-bg text-navy m-0 grid list-none grid-cols-2 gap-2 border p-4 font-mono text-sm"
      >
        {codes.map((code) => (
          <li key={code} className="text-center tracking-wider">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          className="inline-flex items-center gap-2 px-4! py-2! text-sm"
          onClick={() => void handleCopy()}
        >
          <Copy size={14} aria-hidden="true" />
          {copied ? S.COPIED : S.COPY_ALL}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="inline-flex items-center gap-2 px-4! py-2! text-sm"
          onClick={handleDownload}
        >
          <Download size={14} aria-hidden="true" />
          {S.DOWNLOAD}
        </Button>
        {copyFailed && (
          <p role="alert" className="text-danger m-0 text-sm">
            {S.COPY_FAILED}
          </p>
        )}
      </div>
    </div>
  );
}
